import { IWellnessExamCategory } from '@common/models/forms';
import {
  IAddSignatureRequest,
  ISendConsentSMSRequest,
  SetVisitSalesItemQuantityRequest,
  VisitVetUpdateRequest,
  AddSalesItemRequest,
  SetVisitSalesPackageRequest,
  UpdateVisitSalesItemAttributesRequest,
  ISetVisitSalesItemBooleanRequest,
  SignAndStoreConsentRequest,
  VisitStatusUpdateEndpoint,
  IVisitChangeStatusRequest,
  InactivateVisitReminderRequest,
  VisitPetDiagnosisStatusUpdateRequest,
  ICopyVisitSalesItemsRequest,
} from '@common/models/requests';
import { VisitUpdateRequest } from '@common/models/requests/VisitUpdateRequest';
import { IVisitModel, VisitModel } from '@common/models/VisitModel';
import { http } from '@common/utils';
import { isUndefined } from 'lodash';
import { trackPromise } from 'react-promise-tracker';
import { ServiceHelper } from './serviceHelper';
import {
  IPhotoModel,
  IVisitPublicModel,
  PhotoModel,
  VisitNoteModel,
  VisitPublicModel,
  IVisitSalesItemModel,
  VisitSalesItemModel,
  ISalesItemAttributeListValueModel,
  VisitSalesItemAttributeListValueModel,
} from '@common/models';
import {
  BlobEntityType,
  EventTypesNumeric,
  SalesItemAttributeType,
} from '@common/models/Enums';
import { v4 as uuid } from 'uuid';
import { IConsentForm } from '@common/models/forms/ConsentForm';
import { VisitSectionVersionModel } from '@common/models/VisitSectionVersionModel';
import { IVisitSectionTypeModel } from '@common/models/VisitSectionTypeModel';
import { IRabiesNewLotNumberRequest } from '@common/models/requests/RabiesNewLotNumberRequest';
import { IReferralConfig } from '@common/models/VisitExamModel';
import { ISetVaccinationApprovalRequest } from '@common/models/requests/SetVaccinationApprovalRequest';

export class VisitService {
  private static baseUrl: string = '/api/visit';
  private static publicUserHeader: any = {
    vpca: '351BD313-2C19-442B-BC87-C69356EB59EF',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  };

  /**
   * Fetches a visit by ID
   * @param visitId A visit ID
   * @returns
   */
  public static tags = {
    cloneObjectives: 'cloneObjectives',
    getVisit: 'getVisit',
    getPublicVisit: 'getPublicVisit',
    updateVisit: 'updateVisit',
    updateVisitExamResult: 'updateVisitExamResult',
    saveSignature: 'saveSignature',
    saveVisitFollowupNote: 'saveVisitFollowupNote',
    saveVisitFollowupNotes: 'saveVisitFollowupNotes',
    deleteVisitAttachment: 'deleteVisitAttachment',
    resendVisitCompletionEmail: 'resendVisitCompletionEmail',
    signAndStoreConsent: 'signAndStoreConsent',
    getVisitSectionVersions: 'getVisitSectionVersions',
    addNewRabiesLotNumber: 'addNewRabiesLotNumber',
    cancelPrescriptionPickup: 'cancelPrescriptionPickup',
    updateVisitSalesItemOption: 'updateVisitSalesItemOption',
    saveClientSignature: 'saveClientSignature',
    saveVetSignature: 'saveVetSignature',
    declineConsentVisit: 'declineConsentVisit',
    updateVisitStatus: 'updateVisitStatus',
    abortVisit: 'abortVisit',
    copyintaketoclient: 'copyintaketoclient',
    copyIntakeToPet: 'copyIntakeToPet',
    inactivateVisitReminder: 'inactivateVisitReminder',
    notifyClientByVisitId: 'notifyClientByVisitId',
    updateVisitToConsultationStatus: 'updateVisitToConsultationStatus',
    addPetDiagnosis: 'addPetDiagnosis',
    deletePetDiagnosis: 'deletePetDiagnosis',
    makeRegularVisit: 'makeRegularVisit',
    makeRxVisit: 'makeRxVisit',
    updatePetDiagnosisStatus: 'updatePetDiagnosisStatus',
    sendPreVisitNotification: 'sendPreVisitNotification',
    updateVisitToVetServiceStatus: 'updateVisitToVetServiceStatus',
    addRefillSalesItem: 'addRefillSalesItem',
    completeVisit: 'completeVisit',
    copyVisitSalesItems: 'copyVisitSalesItems',
  };

  static cloneObjectives = (
    sourceVisitId: number,
    destinationVisitId: number,
    notes: string | undefined,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) =>
        http(`${this.baseUrl}/cloneobjectives`)
          .post<IVisitModel>({ sourceVisitId, destinationVisitId, notes })
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to clone visit objective observations.',
            );
            reject(err);
          }),
      ),
      this.tags.cloneObjectives,
    );

  static getVisit = (visitId: number): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/${visitId}`)
          .get<IVisitModel>()
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError('Failed to fetch visit.');
            reject(err);
          });
      }),
      this.tags.getVisit,
    );
  };

  static getPublicVisit = (
    visitPublicId: string,
  ): Promise<VisitPublicModel> => {
    return trackPromise(
      new Promise<VisitPublicModel>((resolve, reject) => {
        http(
          `${this.baseUrl}/public/${visitPublicId}?vpca=351BD313-2C19-442B-BC87-C69356EB59EF`,
        )
          .get<IVisitPublicModel>()
          .then((json) => new VisitPublicModel(json))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.getVisit,
    );
  };

  static saveFollowupVisitNote = (
    followupVisitNote: VisitNoteModel,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/addvisitnote`)
          .post<IVisitModel>(followupVisitNote)
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch((err) => reject(err));
      }),
      this.tags.saveVisitFollowupNote,
    );
  };

  static updateVisit = (visit: VisitUpdateRequest): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/update`)
          .post<IVisitModel>(visit)
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static updateVisitExamResult = (
    visitId: number,
    category: IWellnessExamCategory,
  ) => {
    const req = {
      visitId,
      visitExamResultId: category.visitExamResultId,
      examResultTypeId: category.resultType,
      visitExamResultNotes: category.notes,
      examObservationIds: category.observationIds,
    };
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/ensureexamresultobservations`)
          .post<IVisitModel>(req)
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to update visit objective observation.',
            );
            reject(err);
          });
      }),
      this.tags.updateVisitExamResult,
    );
  };

  static setAllResultsToNormal = (visitId: number) => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/setallexamresultstonormal/${visitId}`)
          .post<IVisitModel>({})
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError(
              'Failed to set all visit observations to normal.',
            );
            reject(err);
          });
      }),
      this.tags.updateVisitExamResult,
    );
  };

  static saveVisitSignature = (
    visitId: number,
    photoImage: string,
  ): Promise<PhotoModel> => {
    const encodedImage = photoImage.split(',')[1];
    const filename = `visit-${visitId}-${uuid()}.png`;
    const photoRequest: IAddSignatureRequest = {
      visitId: visitId,
      container: BlobEntityType.Signatures,
      fileName: filename,
      signatureBase64Encoded: encodedImage,
    };
    return trackPromise(
      new Promise<PhotoModel>((resolve, reject) => {
        http(`${this.baseUrl}/savesignature/${visitId}`)
          .post<IPhotoModel>(photoRequest)
          .then((json) => new PhotoModel(json))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError('Failed to save signature.');
            reject(err);
          });
      }),
      this.tags.updateVisit,
    );
  };

  private static createIAddSignatureRequest = (
    visitId: number,
    photoImage: string,
    fileName: string,
  ) => {
    const signatureBase64Encoded = photoImage.split(',')[1];
    return {
      visitId,
      fileName,
      signatureBase64Encoded,
      container: BlobEntityType.Signatures,
    } as IAddSignatureRequest;
  };

  private static createSaveSignaturePromise = (
    visitId: number,
    photoRequest: IAddSignatureRequest,
  ) => {
    return new Promise<PhotoModel>((resolve, reject) => {
      http(`${this.baseUrl}/savesignature/${visitId}`)
        .post<IPhotoModel>(photoRequest)
        .then((json) => new PhotoModel(json))
        .then(resolve)
        .catch((err) => {
          reject(err);
        });
    });
  };

  static saveClientSignature = (
    visitId: number,
    photoImage: string,
  ): Promise<PhotoModel> => {
    const fileName = `visit-${visitId}-client.png`;
    const photoRequest = this.createIAddSignatureRequest(
      visitId,
      photoImage,
      fileName,
    );
    return trackPromise(
      this.createSaveSignaturePromise(visitId, photoRequest),
      this.tags.saveClientSignature,
    );
  };

  static saveVetSignature = (
    visitId: number,
    photoImage: string,
  ): Promise<PhotoModel> => {
    const fileName = `visit-${visitId}-vet.png`;
    const photoRequest = this.createIAddSignatureRequest(
      visitId,
      photoImage,
      fileName,
    );
    return trackPromise(
      this.createSaveSignaturePromise(visitId, photoRequest),
      this.tags.saveVetSignature,
    );
  };

  static updateVisitVet = (
    visitId: number,
    vetId: number | undefined,
    visitStatusId?: number | undefined,
  ): Promise<VisitModel> => {
    const req: VisitVetUpdateRequest = {
      visitId: visitId,
      vetId: vetId,
      vetNotInList: isUndefined(vetId),
      vetLicense: vetId ? 'UseVetIdRecord' : 'VetNotSelected',
      vetName: vetId ? 'UseVetIdRecord' : 'VetNotSelected',
      visitStatusId: visitStatusId,
    };
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/updatevisitvet`)
          .post<IVisitModel>(req)
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static completeVisit = (payload: {
    visitId: number;
    visitCompletionTypeId: number;
    clientEmail: string;
    optOutFromEmail: boolean;
  }): Promise<VisitModel> => {
    const req = {
      VisitId: payload.visitId,
      VisitCompletionTypeId: payload.visitCompletionTypeId,
      ClientEmail: payload.clientEmail,
      OptOutFromEmail: payload.optOutFromEmail,
    };

    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/complete`)
          .post<IVisitModel>(req)
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.completeVisit,
    );
  };

  static addSalesItem = (
    visitId: number,
    salesItemOptionId: number,
    salesItemOptionIdBeingReplaced: number | undefined,
    isPending?: boolean,
    isPackageUpgrade?: boolean,
  ): Promise<VisitModel> => {
    const req = new AddSalesItemRequest({
      visitId,
      salesItemOptionId,
      salesItemOptionIdBeingReplaced,
      ...(isPending !== undefined && { isPending }),
      ...(isPackageUpgrade !== undefined && { isPackageUpgrade }),
    });
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/addsalesitem/${visitId}`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static addSalesItemByRequest = (
    addSalesItemRequest: AddSalesItemRequest,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/addsalesitem/${addSalesItemRequest.visitId}`)
          .post<IVisitModel>(addSalesItemRequest)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static deleteSalesItem = (
    visitId: number,
    salesItemId: number,
  ): Promise<boolean> => {
    return trackPromise(
      new Promise<boolean>((resolve, reject) => {
        http(`${this.baseUrl}/visitsalesitem/${visitId}/${salesItemId}`)
          .delete()
          .then(() => resolve(true))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static setSalesPackage = (
    visitId: number,
    salesPackageId: number | undefined,
  ): Promise<VisitModel> => {
    const req: SetVisitSalesPackageRequest = {
      visitId,
      salesPackageId,
      isFromDI: false,
    };
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/setsalespackage/${visitId}`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static removePackageEntirelyFromVisit = (
    visitId: number,
  ): Promise<VisitModel> => {
    const req: SetVisitSalesPackageRequest = {
      visitId: visitId,
      salesPackageId: undefined,
      isFromDI: false,
    };

    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/setsalespackage/${visitId}`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static addSalesPlan = (
    visitId: number,
    salesPlanId: number,
  ): Promise<VisitModel> => {
    const req = { visitId, salesPlanId };
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/addsalesplan`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static deleteVisitAttachment = (
    visitId: number,
    visitAttachmentId: number,
  ): Promise<boolean> => {
    return trackPromise(
      new Promise<boolean>((resolve, reject) => {
        http(`${this.baseUrl}/${visitId}/attachment/${visitAttachmentId}`)
          .delete()
          .then(() => resolve(true))
          .catch(reject);
      }),
      this.tags.deleteVisitAttachment,
    );
  };

  static updateVisitSalesItemAttributes = (
    visitSalesItem: VisitSalesItemModel,
    bypassVisitTotalsRecalc: boolean,
  ): Promise<VisitSalesItemModel> => {
    // assume all attributes are filled out. the server won't set the flag if they're not.
    visitSalesItem.given = true;
    // only require all attributes if this is a rabies sales item.
    const isRabiesSalesItem = visitSalesItem.visitSalesItemAttributes.some(
      (vsia) => vsia.salesItemAttributeTypeId === SalesItemAttributeType.Rabies,
    );
    const req: UpdateVisitSalesItemAttributesRequest = {
      visitSalesItem,
      evaluateGivenStatus: isRabiesSalesItem,
      bypassVisitTotalsRecalc: bypassVisitTotalsRecalc,
    };
    return trackPromise(
      new Promise<VisitSalesItemModel>((resolve, reject) => {
        http(`${this.baseUrl}/updateattributes`)
          .post<IVisitSalesItemModel>(req)
          .then((json) => resolve(new VisitSalesItemModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  // #TODO
  // Need to rewrite this code once done with the hospital related ticket
  static updateVisitSalesItemAttributesClinic = (
    visitSalesItem: VisitSalesItemModel,
    bypassVisitTotalsRecalc: boolean,
  ): Promise<VisitSalesItemModel> => {
    // only require all attributes if this is a rabies sales item.
    const req: UpdateVisitSalesItemAttributesRequest = {
      visitSalesItem,
      evaluateGivenStatus: true,
      bypassVisitTotalsRecalc: bypassVisitTotalsRecalc,
    };
    return trackPromise(
      new Promise<VisitSalesItemModel>((resolve, reject) => {
        http(`${this.baseUrl}/updateattributes`)
          .post<IVisitSalesItemModel>(req)
          .then((json) => resolve(new VisitSalesItemModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static setVisitSalesItemAsGiven = (
    visitId: number,
    visitSalesItemId: number,
  ): Promise<void> => {
    const req: ISetVisitSalesItemBooleanRequest = {
      visitId,
      visitSalesItemId,
      value: true,
    };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setgivenstatus`)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static setVisitSalesItemAsDeclined = (
    visitId: number,
    visitSalesItemId: number,
    value: boolean = true,
  ): Promise<void> => {
    const req: ISetVisitSalesItemBooleanRequest = {
      visitId,
      visitSalesItemId,
      value,
    };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setdeclinedstatus`)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static setVisitSalesItemAsNotGiven = (
    visitId: number,
    visitSalesItemId: number,
  ): Promise<void> => {
    const req: ISetVisitSalesItemBooleanRequest = {
      visitId,
      visitSalesItemId,
      value: true,
    };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setnotgivenstatus`)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static setVisitSalesItemAsPending = (
    visitId: number,
    visitSalesItemId: number,
    isPending: boolean = true,
  ): Promise<void> => {
    const req: ISetVisitSalesItemBooleanRequest = {
      visitId,
      visitSalesItemId,
      value: isPending,
    };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setpendingstatus`)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static setVisitSalesItemQuantity = ({
    visitSalesItemId,
    quantity,
    estimateQuantityHigh,
    estimateQuantityLow,
    salesItemUnitPrice,
  }: VisitSalesItemModel): Promise<void> => {
    const req: SetVisitSalesItemQuantityRequest = {
      visitSalesItemId,
      quantity,
      estimateQuantityHigh,
      estimateQuantityLow,
      salesItemUnitPrice,
    };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setvisitsalesitemquantity`)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static signAndStoreConsent = (
    signatureBase64Encoded: string,
    visitPublicId: string,
    consentTypePublicId: string,
    isPublic: boolean = false,
    formData?: IConsentForm,
  ): Promise<void> => {
    const req = SignAndStoreConsentRequest.ParseSignAndStoreConsentRequest(
      visitPublicId,
      consentTypePublicId,
      signatureBase64Encoded,
      formData,
    );

    const authHeader = isPublic ? { headers: this.publicUserHeader } : {};
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/signandstoreconsent`)
          .mergeOptions(authHeader)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.signAndStoreConsent,
    );
  };

  static sendConsentSms = (
    visitId: number,
    consentTypeId: number,
    mobileNumber: string,
  ): Promise<void> => {
    const req: ISendConsentSMSRequest = {
      visitId,
      consentTypeId,
      cellPhone: mobileNumber,
    };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/sendconsentsms`)
          .post<void>(req)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };

  static resendVisitCompletionEmail = (
    visitPublicIds: string[],
  ): Promise<void> => {
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/sendvisitcompletionbulk`)
          .mergeOptions({ headers: this.publicUserHeader })
          .post<void>(visitPublicIds)
          .then(resolve)
          .catch(reject);
      }),
      this.tags.resendVisitCompletionEmail,
    );
  };

  static getVisitSectionVersions = (
    visitId: number,
  ): Promise<VisitSectionVersionModel[]> =>
    trackPromise(
      new Promise<VisitSectionVersionModel[]>((resolve, reject) => {
        http(`${this.baseUrl}/visitsectionversions/${visitId}`)
          .get<IVisitSectionTypeModel[]>()
          .then((data) => data.map((d) => new VisitSectionVersionModel(d)))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.getVisitSectionVersions,
    );

  static addVisitDiagnosis = (
    visitId: number,
    diagnosisId: number,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/${visitId}/diagnosis`)
          .post<IVisitModel>({ visitId, diagnosisId })
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  static deleteVisitDiagnosis = (
    visitId: number,
    diagnosisId: number,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/${visitId}/diagnosis/${diagnosisId}`)
          .deleteWithResponse<IVisitModel>()
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  static addNewRabiesLotNumber = (
    visitSalesItemId: number,
    salesItemId: number,
    lotNumber: IRabiesNewLotNumberRequest,
  ): Promise<VisitSalesItemAttributeListValueModel> =>
    trackPromise(
      new Promise<VisitSalesItemAttributeListValueModel>((resolve, reject) => {
        http(
          `${this.baseUrl}/addNewRabiesSerial/${visitSalesItemId}/${salesItemId}`,
        )
          .post<ISalesItemAttributeListValueModel>(lotNumber)
          .then((data) => new VisitSalesItemAttributeListValueModel(data))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.addNewRabiesLotNumber,
    );

  static validateVisitWithSoftWarnings = (visit: VisitModel): boolean => {
    var idexxTests = visit.labOrders
      .map((l) => l.tests.flatMap((t) => t.testCode))
      .flat();
    var testSalesItems = visit.visitSalesItems
      .filter((vsi) => vsi.given && vsi.salesItemSupplierProductCode)
      .map((vsi) => vsi.salesItemSupplierProductCode);

    //var idexxTestsNotInSalesItems = idexxTests.filter(t => !testSalesItems.includes(t));
    var salesItemsNotInIdexxTests = testSalesItems.filter(
      (t) => !idexxTests.includes(t),
    );

    return !(salesItemsNotInIdexxTests.length > 0);
  };

  static cancelPrescriptionPickup = (visitId: number): Promise<null> => {
    return trackPromise(
      new Promise<null>((resolve, reject) => {
        http(`${this.baseUrl}/cancelPrescriptionPickup/${visitId}`)
          .post<null>({})
          .then(resolve)
          .catch(reject);
      }),
      this.tags.cancelPrescriptionPickup,
    );
  };

  static updateVisitSalesItemOption = (
    visitId: number,
    oldSalesItemOptionId: number,
    newSalesItemOptionId: number,
    reminderDays: number = 0,
  ): Promise<VisitModel> => {
    const req = new AddSalesItemRequest({
      visitId,
      salesItemOptionId: newSalesItemOptionId,
      salesItemOptionIdBeingReplaced: oldSalesItemOptionId,
      reminderDays: reminderDays,
    });
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/updateVisitSalesItemOption/${visitId}`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisitSalesItemOption,
    );
  };

  static declineConsentVisit = (visitId: number): Promise<void> => {
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/declineconsent/${visitId}`)
          .post({})
          .then(resolve)
          .catch(reject);
      }),
      this.tags.declineConsentVisit,
    );
  };

  static updateVisitStatus = (
    visitId: number,
    endpointName: VisitStatusUpdateEndpoint,
  ): Promise<void> => {
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/${endpointName}/${visitId}/false`)
          .get()
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisitStatus,
    );
  };

  static abortVisit = (
    abortVisitRequest: IVisitChangeStatusRequest,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/abort`)
          .post<IVisitModel>(abortVisitRequest)
          .then((json) => resolve(new VisitModel(json)))
          .catch((err) => {
            reject(err);
          });
      }),
      this.tags.abortVisit,
    );
  };

  static copyIntakeToClient = (visitId: number): Promise<VisitModel> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/copyintaketoclient/${visitId}`)
          .post<IVisitModel>({})
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.copyintaketoclient,
    );
  };

  static copyIntakeToPet = (visitId: number): Promise<void> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/copyintaketopet/${visitId}`)
          .post({})
          .then(resolve)
          .catch((err) => {
            reject(err);
          });
      }),
      this.tags.copyIntakeToPet,
    );
  };

  static copyVisitSalesItems = (
    copyRequest: ICopyVisitSalesItemsRequest,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/copyvisitsalesitems/${copyRequest.visitId}`)
          .post<IVisitModel>(copyRequest)
          .then((json) => {
            resolve(new VisitModel(json));
          })
          .catch((err) => {
            reject(err);
          });
      }),
      this.tags.copyVisitSalesItems,
    );
  };

  static inactivateVisitReminder = (
    inactivateReminderRequest: InactivateVisitReminderRequest,
  ): Promise<void> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/inactiveReminder`)
          .post(inactivateReminderRequest)
          .then(resolve)
          .catch((err) => reject(err));
      }),
      this.tags.inactivateVisitReminder,
    );
  };

  static notifyClient = (
    visitId: number,
    eventType: EventTypesNumeric,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/notifyclient/${visitId}/${eventType}`)
          .post<IVisitModel>({})
          .then((json) => resolve(new VisitModel(json)))
          .catch((err) => {
            reject(err);
          });
      }),
      this.tags.notifyClientByVisitId,
    );
  };

  static updateVisitToConsultationStatus = (
    visitId: number,
    includeSiblings: boolean = true,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/consultation/${visitId}/${includeSiblings}`)
          .get<IVisitModel>()
          .then((json) => resolve(new VisitModel(json)))
          .catch((err) => {
            reject(err);
          });
      }),
      this.tags.updateVisitToConsultationStatus,
    );
  };

  static updateVisitToVetServiceStatus = (
    visitId: number,
    includeSiblings: boolean = true,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise((resolve, reject) => {
        http(`${this.baseUrl}/vetservices/${visitId}/${includeSiblings}`)
          .get<IVisitModel>()
          .then((json) => resolve(new VisitModel(json)))
          .catch((err) => {
            reject(err);
          });
      }),
      this.tags.updateVisitToVetServiceStatus,
    );
  };

  static addPetDiagnosis = (
    visitId: number,
    diagnosisId: number,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/addPetDiagnosis`)
          .post<IVisitModel>({ visitId, diagnosisId })
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  static deletePetDiagnosis = (
    visitId: number,
    petDiagnosisId: number,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/${visitId}/petDiagnosis/${petDiagnosisId}`)
          .deleteWithResponse<IVisitModel>()
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  static makeRegularVisit = (
    visitId: number,
    isFromDI?: boolean,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        const payload = {
          visitId,
          isFromDI: isFromDI ?? false,
        };

        http(`${this.baseUrl}/makeregularvisit`)
          .post<IVisitModel>(payload)
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  static makeRxVisit = (
    visitId: number,
    isFromDI?: boolean,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        const payload = { visitId, isFromDI: !!isFromDI };
        http(`${this.baseUrl}/makerxvisit`)
          .post<IVisitModel>(payload)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  static updatePetDiagnosisStatus = (
    petDiagnosisUpdateRequest: VisitPetDiagnosisStatusUpdateRequest,
  ): Promise<VisitModel> =>
    trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/updatePetDiagnosisStatusId`)
          .post<IVisitModel>(petDiagnosisUpdateRequest)
          .then((data) => new VisitModel(data))
          .then(resolve)
          .catch(reject);
      }),
      this.tags.updateVisit,
    );

  /**
   * Sends the pre-visit ("Are You Here?") notification SMS for a reservation.
   * Always allowed to be sent (server will update PreVisitSMSDate each time).
   * @param reservationId Reservation identifier
   */
  static sendPreVisitNotification = (reservationId: number): Promise<void> => {
    const req = { reservationId };
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/sendprevisitnotification`)
          .post<void>(req)
          .then(() => resolve())
          .catch((err) => reject(err));
      }),
      this.tags.sendPreVisitNotification,
    );
  };

  static ensureProactivePrescriptions = (
    visitId: number,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/${visitId}/ensurehospitalproactiveprescriptions`)
          .post<IVisitModel>({})
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError('Failed to add proactive prescriptions.');
            reject(err);
          });
      }),
      this.tags.updateVisit,
    );
  };

  static ensureClinicProactivePrescriptions = (
    visitId: number,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/ensurepetprotections/${visitId}`)
          .post<IVisitModel>({})
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError('Failed to add proactive prescriptions.');
            reject(err);
          });
      }),
      this.tags.updateVisit,
    );
  };

  static enterVisitAdminMode = (
    visitId: number,
    payload: {
      visitId: number;
      notes: string;
      isPrivateNote: boolean;
      followUpTypeId: number;
    },
  ): Promise<any> => {
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setvisitadminmode/${visitId}`)
          .post(payload)
          .then(() => resolve())
          .catch((err) => {
            ServiceHelper.showError('Failed to enter admin mode.');
            reject(err);
          });
      }),
      this.tags.updateVisit,
    );
  };

  static exitVisitAdminMode = (visitId: number): Promise<any> => {
    return trackPromise(
      new Promise<void>((resolve, reject) => {
        http(`${this.baseUrl}/setvisitnonadminmode/${visitId}`)
          .post({})
          .then(() => resolve())
          .catch((err) => {
            ServiceHelper.showError('Failed to exit admin mode.');
            reject(err);
          });
      }),
      this.tags.updateVisit,
    );
  };

  static setVisitReferrals = (
    request: IReferralConfig,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/setvisitreferrals`)
          .post<IVisitModel>(request)
          .then((json) => new VisitModel(json))
          .then(resolve)
          .catch((err) => {
            ServiceHelper.showError('Failed to save visit referrals.');
            reject(err);
          });
      }),
    );
  };

  static addRefillSalesItem = (
    req: AddSalesItemRequest,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/addRefillSalesItem/${req.visitId}`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.addRefillSalesItem,
    );
  };

  static setVaccinationApproval = (
    req: ISetVaccinationApprovalRequest,
  ): Promise<VisitModel> => {
    return trackPromise(
      new Promise<VisitModel>((resolve, reject) => {
        http(`${this.baseUrl}/setvaccinationapproval`)
          .post<IVisitModel>(req)
          .then((json) => resolve(new VisitModel(json)))
          .catch(reject);
      }),
      this.tags.updateVisit,
    );
  };
}
